import { Stack } from "@mui/material";
import { FirebaseError } from "firebase/app";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "src/contexts/AppContext";
import { useAuth } from "src/contexts/AuthContext";
import requestLoginCode, {
  LoginCodeChannel,
} from "src/features/users/services/requestLoginCode";
import verifyLoginCode from "src/features/users/services/verifyLoginCode";
import generateVerificationRequestId from "src/features/users/utils/generateVerificationRequestId";
import loginCodeErrorMessage from "src/features/users/utils/loginCodeErrorMessage";
import { trackEvent } from "src/hooks/useInstrumentation";
import useErrorHandler from "src/hooks/utility/useErrorHandler";
import SubmitButton from "src/shared/components/Button/SubmitButton";
import LoginLinkAction from "./LoginLinkAction";
import LoginStepLayout from "./LoginStepLayout";
import { loginPrimaryButtonSx } from "./loginStyles";
import OneTimeCodeInput from "./OneTimeCodeInput";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

interface Props {
  identifier: string;
  channel: LoginCodeChannel;
  onSignedIn: () => void;
  onUseDifferentIdentifier: () => void;
}

export default function CodeStep({
  identifier,
  channel,
  onSignedIn,
  onUseDifferentIdentifier,
}: Props): JSX.Element {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const verificationRequestId = useRef(generateVerificationRequestId());
  const authCtx = useAuth();
  const { clients } = useAppContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleError = useErrorHandler();

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleVerify = async () => {
    setError(null);
    try {
      setLoading(true);
      const { customToken } = await verifyLoginCode(
        {
          identifier: identifier.trim(),
          code,
          requestId: verificationRequestId.current,
        },
        clients.functions
      );
      await authCtx.loginWithToken(customToken);
      trackEvent("Login Screen - Code Sign In Completed", { channel });
      onSignedIn();
    } catch (e) {
      setLoading(false);
      if (e instanceof FirebaseError) {
        setError(loginCodeErrorMessage(e, t, "verify"));
      } else handleError(e);
    }
  };

  const handleResend = async () => {
    setError(null);
    setResent(false);
    try {
      setResending(true);
      await requestLoginCode(
        { identifier: identifier.trim() },
        clients.functions
      );
      setResent(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (e) {
      if (e instanceof FirebaseError) {
        setError(loginCodeErrorMessage(e, t, "request"));
      } else handleError(e);
    } finally {
      setResending(false);
    }
  };

  return (
    <LoginStepLayout
      title={
        channel === "sms" ? t("Check your texts.") : t("Check your inbox.")
      }
      description={t(
        "We sent a 6-digit code to {{identifier}}. Enter it below.",
        { identifier: identifier.trim() }
      )}
      onBack={onUseDifferentIdentifier}
    >
      <Stack
        component="form"
        spacing={{ xs: 3, sm: 3.5 }}
        onSubmit={(e) => {
          e.preventDefault();
          handleVerify();
        }}
      >
        <OneTimeCodeInput
          value={code}
          error={!!error}
          helperText={error ?? (resent ? t("New code sent.") : undefined)}
          onChange={(nextCode) => {
            setError(null);
            setCode(nextCode);
          }}
        />

        <SubmitButton
          fullWidth
          disabled={code.length !== CODE_LENGTH}
          loading={loading}
          sx={loginPrimaryButtonSx}
        >
          {t("Sign In")}
        </SubmitButton>

        <Stack spacing={0.75} alignItems="center">
          <LoginLinkAction
            onClick={handleResend}
            disabled={cooldown > 0 || resending}
          >
            {cooldown > 0
              ? t("Resend in {{seconds}}s", { seconds: cooldown })
              : t("Resend code")}
          </LoginLinkAction>
          <LoginLinkAction onClick={() => navigate("/register")}>
            {t("Register for a new account")}
          </LoginLinkAction>
        </Stack>
      </Stack>
    </LoginStepLayout>
  );
}
